// California Private Car Sale Guides — article data
// Tone: clear, practical, trustworthy. Not legal advice.

const DISCLAIMER = "CarSaleDoc is not a law firm and does not provide legal advice. This guide is for general informational purposes only. DMV requirements may vary depending on the vehicle, title status and transaction. Always check official California DMV requirements before completing a vehicle transfer.";

const CTA = { label: "Create California Car Bill of Sale", to: "/builder/new" };

// Block types rendered by ArticlePage:
//  { kind: "p", html: "..." }
//  { kind: "h2", text: "..." }
//  { kind: "list", items: ["...", "..."] }
//  { kind: "callout", text: "..." }
//  { kind: "cta" }

export const CA_GUIDES = [
  {
    slug: "private-sale-checklist",
    title: "California Private Car Sale Checklist",
    metaTitle: "California Private Car Sale Checklist | Buyer & Seller Steps",
    metaDescription:
      "Use this California private car sale checklist to understand buyer and seller steps, including bill of sale, title transfer, odometer reading, release of liability and documents to keep.",
    cardDescription: "Step-by-step list for buyers and sellers — bill of sale, title transfer, odometer, release of liability, and documents to keep.",
    h1: "California Private Car Sale Checklist",
    related: ["title-transfer", "release-of-liability"],
    heroVideo: {
      id: "-GKC_UlEEnY",
      title: "California Private Car Sale Checklist — Buyer & Seller Steps",
      description:
        "Full walkthrough of the California DMV process for a private car sale: bill of sale, title transfer, odometer reading, release of liability (REG 138) and smog check.",
      uploadDate: "2026-06-29T00:00:00+00:00",
      durationISO: "PT2M30S",
    },
    blocks: [
      { kind: "p", html: "Selling or buying a car privately in California means documenting the transaction between the two parties and completing official DMV steps for the title transfer and release of liability. This checklist walks you through what each side typically needs to do." },
      { kind: "p", html: "A signed bill of sale documents the agreement: who is selling, who is buying, the vehicle described, the price, the odometer reading and the terms. It does not replace official DMV forms, but it is the primary record both parties keep of the deal." },
      { kind: "h2", text: "Seller checklist" },
      { kind: "list", items: [
        "Gather your vehicle details: VIN, license plate, year, make, model, trim.",
        "Take a clear photo of the odometer at the moment of sale.",
        "Prepare the title and confirm your name appears as the registered owner.",
        "Fill out and sign a bill of sale documenting the buyer, the price, the odometer and as-is terms.",
        "Hand over the signed title and any required transfer documents to the buyer.",
        "Notify the California DMV within 5 days of the sale by filing a Notice of Transfer and Release of Liability.",
        "Keep a copy of the bill of sale and your NRL confirmation for your records.",
      ] },
      { kind: "h2", text: "Buyer checklist" },
      { kind: "list", items: [
        "Verify the VIN on the vehicle matches the title and registration.",
        "Inspect the vehicle and confirm the odometer reading.",
        "Sign the bill of sale together with the seller and keep your copy.",
        "Receive the signed title and any required odometer or transfer forms.",
        "Complete the title transfer at the California DMV within 10 days of the sale and pay the applicable fees.",
        "Keep copies of the bill of sale, the signed title, payment receipts and any related documents.",
      ] },
      { kind: "h2", text: "Documents to keep" },
      { kind: "list", items: [
        "Signed bill of sale (PDF or printed copy).",
        "Photos of the vehicle, VIN plate and odometer at sale time.",
        "Title or transfer documents copy.",
        "Payment confirmation (bank transfer receipt, cashier's check stub, etc.).",
        "Notice of Transfer and Release of Liability confirmation (seller).",
        "DMV title transfer receipts (buyer).",
      ] },
      { kind: "callout", text: "CarSaleDoc helps you create the bill of sale, capture odometer and photo attachments, and produce a signed PDF for both parties. It does not replace DMV forms such as title transfer, REG 135, REG 262 or the Notice of Transfer and Release of Liability." },
      { kind: "cta" },
    ],
    faqs: [
      { q: "Do I need a bill of sale for a private car sale in California?", a: "California DMV indicates that a bill of sale may be acceptable if it clearly identifies the vehicle or vessel. The standard official form is REG 135. A bill of sale documents the transaction between buyer and seller and is recommended in addition to the official title transfer paperwork." },
      { q: "How long does the buyer have to transfer ownership in California?", a: "Buyers typically have 10 days from the date of sale to transfer ownership at the California DMV and pay the applicable fees. Acting promptly avoids late penalties." },
      { q: "How long does the seller have to notify the DMV after selling a car?", a: "Sellers in California must notify the DMV within 5 days of the sale by submitting a Notice of Transfer and Release of Liability." },
      { q: "Does CarSaleDoc replace DMV forms?", a: "No. CarSaleDoc creates a signed bill of sale between buyer and seller. It does not file a title transfer, a release of liability, REG 135, REG 262 or any other official DMV form." },
    ],
  },

  {
    slug: "what-to-include",
    title: "What to Include in a California Car Bill of Sale",
    metaTitle: "What to Include in a California Car Bill of Sale",
    metaDescription:
      "Learn what information to include in a California car bill of sale, including buyer and seller details, VIN, sale price, odometer, as-is terms and signatures.",
    cardDescription: "The exact fields and clauses a California bill of sale should contain — VIN, price, odometer, as-is, signatures and attachments.",
    h1: "What Should a California Car Bill of Sale Include?",
    related: ["odometer-disclosure", "as-is-sale"],
    blocks: [
      { kind: "p", html: "A bill of sale should leave no doubt about who is selling, who is buying, which vehicle is involved and under what terms. Done right, it protects both parties if any question comes up later." },
      { kind: "h2", text: "Buyer and seller information" },
      { kind: "list", items: [
        "Full legal names of both parties.",
        "Addresses (street, city, state, ZIP).",
        "Phone numbers and email addresses for contact.",
      ] },
      { kind: "h2", text: "Vehicle description" },
      { kind: "list", items: [
        "Year, make, model and trim.",
        "VIN (17 characters).",
        "License plate number.",
        "Body type and color.",
        "Title number, if available.",
      ] },
      { kind: "h2", text: "Sale terms" },
      { kind: "list", items: [
        "Sale price (in USD).",
        "Payment method: cash, cashier's check, bank transfer, money order, etc.",
        "Sale date.",
        "Delivery date and location, if different from the sale date.",
        "Any deposit paid and balance due.",
      ] },
      { kind: "h2", text: "Odometer information" },
      { kind: "list", items: [
        "Odometer reading (in miles or kilometers).",
        "Date of the reading.",
        "Odometer status: actual mileage, not actual mileage, exceeds mechanical limits, or unknown / discrepancy.",
      ] },
      { kind: "h2", text: "As-is or warranty terms" },
      { kind: "p", html: 'An "as-is" clause documents that the buyer is accepting the vehicle in its current condition with no written warranty from the seller, unless something is explicitly written into the agreement. If you are offering a limited warranty, write the specific terms in the document.' },
      { kind: "h2", text: "Known defects" },
      { kind: "p", html: "Listing known damage, mechanical issues or wear avoids disputes later. Keep it factual — the goal is shared documentation, not a sales pitch." },
      { kind: "h2", text: "Signatures" },
      { kind: "list", items: [
        "Buyer signature and printed name.",
        "Seller signature and printed name.",
        "Date next to each signature.",
      ] },
      { kind: "h2", text: "Attachments and exhibits" },
      { kind: "list", items: [
        "Title photo (front and back).",
        "Odometer photo at the sale moment.",
        "VIN plate photo.",
        "Exterior and interior condition photos.",
        "Photos of any damage disclosed in writing.",
        "Payment receipt or transfer confirmation.",
      ] },
      { kind: "callout", text: "CarSaleDoc collects all of the above and produces a clean, signed PDF you and the other party can keep for your records." },
      { kind: "cta" },
    ],
    faqs: [
      { q: "What information should be in a California car bill of sale?", a: "Buyer and seller details, vehicle description (year, make, model, VIN, plate), sale price and payment method, sale and delivery dates, odometer information, as-is or warranty terms, known defects and signatures of both parties." },
      { q: "Should I include the VIN?", a: "Yes. The VIN uniquely identifies the vehicle and is essential for any future dispute, title transfer or registration. The standard VIN is 17 characters." },
      { q: "Should both buyer and seller sign?", a: "Yes. Both parties should sign and date the bill of sale. A signed document is what gives the bill of sale its evidentiary value." },
      { q: "Can I attach photos?", a: "Yes. CarSaleDoc lets you attach exterior, interior, odometer, VIN and damage photos as labeled exhibits in the final PDF, which strengthens the record of the vehicle's condition at sale time." },
    ],
  },

  {
    slug: "odometer-disclosure",
    title: "California Odometer Disclosure for Private Car Sales",
    metaTitle: "California Odometer Disclosure for Private Car Sales",
    metaDescription:
      "Understand odometer disclosure in a California private car sale and how to include mileage information in your vehicle bill of sale.",
    cardDescription: "How to document mileage at sale time and when California requires official odometer forms.",
    h1: "Odometer Disclosure in a California Private Car Sale",
    related: ["what-to-include"],
    blocks: [
      { kind: "p", html: "The odometer reading documents how many miles the vehicle has traveled at the moment it changes hands. It is one of the most disputed facts in private vehicle sales, so getting it right protects both buyer and seller." },
      { kind: "h2", text: "What is an odometer reading?" },
      { kind: "p", html: "The odometer reading is the mileage shown on the vehicle's instrument cluster at the time of sale. Take a clear photo of the dashboard when both parties are together — it's the simplest way to lock in the fact." },
      { kind: "h2", text: "Odometer status options" },
      { kind: "list", items: [
        "Actual mileage — the reading reflects true miles traveled.",
        "Not actual mileage — the odometer was rolled back, swapped or otherwise does not reflect true miles.",
        "Exceeds mechanical limits — the odometer reached its maximum value (often 99,999) and rolled over.",
        "Unknown / discrepancy — the reading may not be reliable.",
      ] },
      { kind: "h2", text: "Why both parties should confirm" },
      { kind: "p", html: "Buyer and seller should look at the dashboard together and agree on the number written in the bill of sale. A photo signed off at the same time removes ambiguity." },
      { kind: "h2", text: "When California may require official forms" },
      { kind: "p", html: "The California DMV uses REG 262 (Vehicle/Vessel Transfer and Reassignment) for odometer disclosure in specific situations: when the title is not available, when there's an error or alteration in the odometer section of the title, when the title does not comply with current standards, or in cases of multiple transfers. REG 262 is not available online — it's typically issued by the DMV or licensed dealers." },
      { kind: "callout", text: "CarSaleDoc lets you record the odometer reading, the date, the status and attach an odometer photo to the bill of sale. It does not replace REG 262 or other official DMV odometer forms when those are required." },
      { kind: "cta" },
    ],
    faqs: [
      { q: "What is an odometer disclosure?", a: "An odometer disclosure is a written statement of the vehicle's mileage at the time of transfer. It is required in most US states during a private vehicle sale, with limited exceptions for older or specialty vehicles." },
      { q: "What does actual mileage mean?", a: 'Selecting "actual mileage" means the seller affirms that the reading on the odometer reflects the true miles traveled by the vehicle to the best of their knowledge.' },
      { q: "What does exceeds mechanical limits mean?", a: "It means the odometer has reached its maximum displayable value (commonly 99,999 miles) and rolled back to zero. Older vehicles with high mileage often fall into this category." },
      { q: "Does CarSaleDoc replace official odometer forms?", a: "No. CarSaleDoc includes odometer reading and status in your bill of sale and lets you attach an odometer photo. It does not replace REG 262 or any other official DMV odometer disclosure form when those are required." },
    ],
  },

  {
    slug: "title-transfer",
    title: "California Title Transfer vs Bill of Sale",
    metaTitle: "California Car Title Transfer vs Bill of Sale",
    metaDescription:
      "Learn the difference between a California car bill of sale and a title transfer, and why a bill of sale does not replace DMV transfer requirements.",
    cardDescription: "Why a bill of sale and a title transfer are NOT the same — and why you need both.",
    h1: "California Title Transfer vs Bill of Sale",
    related: ["release-of-liability"],
    blocks: [
      { kind: "p", html: "A bill of sale and a title transfer are two different documents that do two different things. Confusing them is one of the most common mistakes in private vehicle sales." },
      { kind: "h2", text: "What a bill of sale does" },
      { kind: "list", items: [
        "Documents the private agreement between the buyer and the seller.",
        "Captures the vehicle, sale price, odometer, as-is terms and signatures of both parties.",
        "Acts as the buyer's proof of purchase and the seller's proof of sale.",
      ] },
      { kind: "h2", text: "What a title transfer does" },
      { kind: "list", items: [
        "Updates the California DMV's official ownership records.",
        "Legally moves the vehicle's registered owner from the seller to the buyer.",
        "Triggers applicable taxes, fees and the issuance of new registration in the buyer's name.",
      ] },
      { kind: "h2", text: "Who is responsible and by when" },
      { kind: "p", html: "In California, the buyer typically has 10 days from the date of sale to complete the title transfer at the DMV and pay the required fees. Separately, the seller must notify the DMV within 5 days by submitting a Notice of Transfer and Release of Liability." },
      { kind: "callout", text: "A bill of sale does not replace title transfer. You usually need both — the bill of sale for the private record between the parties, and the title transfer to make the change of ownership official with the California DMV." },
      { kind: "cta" },
    ],
    faqs: [
      { q: "Is a bill of sale the same as a title transfer?", a: "No. A bill of sale documents the agreement between buyer and seller. A title transfer updates the official ownership records at the California DMV. Both are usually needed for a complete private sale." },
      { q: "Does the buyer need to transfer the title?", a: "Yes. In California, buyers typically have 10 days from the date of sale to transfer ownership at the DMV and pay applicable fees." },
      { q: "Does the seller need to notify the DMV?", a: "Yes. The seller must file a Notice of Transfer and Release of Liability with the California DMV within 5 days of the sale." },
      { q: "Can I use CarSaleDoc instead of DMV forms?", a: "No. CarSaleDoc creates the bill of sale between the two parties. You still need to complete the official title transfer at the California DMV and submit the Notice of Transfer and Release of Liability." },
    ],
  },

  {
    slug: "release-of-liability",
    title: "California Release of Liability After Selling a Car",
    metaTitle: "California Release of Liability After Selling a Car",
    metaDescription:
      "Learn what the California Notice of Transfer and Release of Liability is, why sellers use it, and what information is typically needed.",
    cardDescription: "Why sellers must notify the DMV within 5 days — and the exact info needed.",
    h1: "California Notice of Transfer and Release of Liability",
    related: ["title-transfer", "private-sale-checklist"],
    blocks: [
      { kind: "p", html: "After selling a vehicle privately in California, the seller has one important step left: notifying the DMV so the state knows you are no longer responsible for the vehicle." },
      { kind: "h2", text: "What it is" },
      { kind: "p", html: "The Notice of Transfer and Release of Liability (NRL) tells the California DMV that you have sold or transferred the vehicle. Once filed, the seller is generally released from civil and parking liability for events that occur after the sale date." },
      { kind: "h2", text: "When to file" },
      { kind: "p", html: "Sellers in California must submit the NRL within 5 days of the sale. The form can be filed online through the California DMV's website." },
      { kind: "h2", text: "Information typically needed" },
      { kind: "list", items: [
        "New owner's name and address.",
        "License plate number of the vehicle.",
        "Last five digits of the VIN.",
        "Odometer reading at the time of sale.",
        "Sale date.",
      ] },
      { kind: "h2", text: "Save your confirmation" },
      { kind: "p", html: "The California DMV recommends keeping the confirmation page (or printed receipt) after submitting the NRL. If a question arises later about responsibility for the vehicle, that confirmation is your proof." },
      { kind: "h2", text: "Important: NRL is not a title transfer" },
      { kind: "p", html: "Filing the Notice of Transfer and Release of Liability does not transfer ownership of the vehicle. The buyer must still complete the DMV's title transfer requirements and pay applicable fees within 10 days of the sale." },
      { kind: "callout", text: "CarSaleDoc collects buyer and seller information, the vehicle details, the odometer reading and the sale date — the same data the California DMV asks for on the NRL. CarSaleDoc does not file the NRL with the DMV on your behalf." },
      { kind: "cta" },
    ],
    faqs: [
      { q: "What is a California Notice of Transfer and Release of Liability?", a: "It is a notification the seller files with the California DMV after selling a vehicle. It tells the state that the seller is no longer responsible for the vehicle and starts the release-of-liability process." },
      { q: "When should the seller file it?", a: "Within 5 days of the sale. It can be filed online through the California DMV's website." },
      { q: "What information is needed?", a: "Typically: new owner's name and address, license plate number, last five digits of the VIN, the odometer reading at the time of sale, and the sale date." },
      { q: "Does it transfer ownership?", a: "No. The NRL releases the seller from liability but does not change the DMV's ownership records. The buyer must still complete the title transfer within 10 days of the sale." },
    ],
  },

  {
    slug: "as-is-sale",
    title: "As-Is Car Sale in California",
    metaTitle: "As-Is Car Sale in California | What It Means",
    metaDescription:
      "Learn what an as-is car sale means in California private vehicle transactions and how to document condition, known defects and buyer acknowledgment.",
    cardDescription: "What 'as-is' actually means in plain English, what to document, and what it doesn't cover.",
    h1: 'What Does "As-Is" Mean in a California Car Sale?',
    related: ["what-to-include"],
    blocks: [
      { kind: "p", html: 'In private vehicle sales, "as-is" is one of the most common — and most misunderstood — terms. Documented well, it sets expectations on both sides and avoids disputes later.' },
      { kind: "h2", text: 'What "as-is" means in plain English' },
      { kind: "list", items: [
        "The buyer is accepting the vehicle in its current condition at the time of sale.",
        "The seller is not offering a warranty unless something is explicitly written into the agreement.",
        "Both parties acknowledge that the vehicle has been inspected (or that the buyer had the opportunity to inspect it) before signing.",
      ] },
      { kind: "h2", text: "Documenting known defects" },
      { kind: "p", html: "Writing known defects into the bill of sale is the easiest way to prove that the buyer was informed. Be specific: scratch on the rear bumper, AC needs recharge, tire wear pattern, check-engine light on, etc. Vague descriptions create more arguments than they solve." },
      { kind: "h2", text: "Buyer inspection" },
      { kind: "p", html: "Whenever possible, the buyer should inspect the vehicle in person or have a qualified mechanic do a pre-purchase inspection. The bill of sale can state that the buyer has inspected the vehicle or had the opportunity to inspect it before signing." },
      { kind: "h2", text: "Attach photos to document condition" },
      { kind: "list", items: [
        "Exterior photos from multiple angles.",
        "Interior photos including seats, dashboard and trunk.",
        "Damage photos for any disclosed issues.",
        "Odometer photo at sale time.",
        "VIN plate photo.",
      ] },
      { kind: "callout", text: 'An "as-is" clause is a documented agreement between the two parties about the vehicle\'s condition at sale time. It does not remove every legal obligation a seller may have under California law, and it is not legal advice. If you are unsure, consult a qualified professional.' },
      { kind: "cta" },
    ],
    faqs: [
      { q: "What does as-is mean when selling a car?", a: 'An "as-is" sale means the buyer accepts the vehicle in its current condition, with no warranty from the seller unless explicitly written into the agreement.' },
      { q: "Should known defects be written down?", a: "Yes. Documenting known damage, mechanical issues or wear avoids disputes after the sale and shows both parties were on the same page." },
      { q: "Can I add photos to document condition?", a: "Yes. CarSaleDoc lets you attach exterior, interior, damage, odometer and VIN photos as labeled exhibits in the final PDF." },
      { q: "Does as-is remove every legal obligation?", a: 'No. An "as-is" clause documents the agreement between the parties about the vehicle\'s condition at sale, but it does not remove every legal obligation a seller may have. Consult a qualified professional for legal advice.' },
    ],
  },
];

export const ARTICLE_DISCLAIMER = DISCLAIMER;
export const ARTICLE_CTA = CTA;

export function getArticle(slug) {
  return CA_GUIDES.find((a) => a.slug === slug);
}
